'use client';

import { useState, useCallback } from 'react';
import { VStack, HStack } from '@terra/ui-styled-system';
import {
  BodyStrong,
  Field,
  Input,
  McpCredentialFields,
  MetaDefault,
  SettingsBooleanControl,
  SettingsEditDialog,
  toasterApi,
} from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import type {
  McpServerResponseDto,
  CreateMcpServerDto,
  UpsertMcpCredentialDto,
} from '@terra/shared-contracts-mcp-servers';
import {
  toMcpFormCredentialType,
  type McpFormCredentialType,
  waitForMcpOAuthPopupCallback,
} from '@terra/shared-contracts-mcp-servers';
import { resolveMcpOAuthScopeDiscovery } from '@terra/shared-rest-clients-mcp-servers';
import {
  useCreateMcpServer,
  useUpdateMcpServer,
  useUpsertMcpCredential,
  useAuthorizeMcpServer,
  useCompleteMcpOAuthCallback,
  useTestMcpServer,
} from '../../hooks/api/useMcpServers';

type McpServerFormDialogProps = {
  server: McpServerResponseDto | null;
  onClose: () => void;
};

export function McpServerFormDialog({
  server,
  onClose,
}: McpServerFormDialogProps) {
  const t = useTranslations('settings.mcpServerForm');
  const [persistedServerId, setPersistedServerId] = useState<string | null>(
    server?.id ?? null,
  );
  const isEditing = Boolean(server || persistedServerId);
  const [name, setName] = useState(server?.name ?? '');
  const [url, setUrl] = useState(server?.url ?? '');
  const [requiresApproval, setRequiresApproval] = useState(
    server?.requiresApproval ?? true,
  );
  const [credentialType, setCredentialType] = useState<McpFormCredentialType>(
    server ? toMcpFormCredentialType(server.credentialType) : 'OAUTH_DCR',
  );
  const [draftCredential, setDraftCredential] =
    useState<UpsertMcpCredentialDto | null>(null);
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const createMutation = useCreateMcpServer();
  const updateMutation = useUpdateMcpServer();
  const credentialMutation = useUpsertMcpCredential();
  const authorizeMutation = useAuthorizeMcpServer();
  const completeOAuthCallbackMutation = useCompleteMcpOAuthCallback();
  const testMutation = useTestMcpServer();
  const handleDiscoverOAuthScopes = useCallback(
    (discoveryUrl: string) =>
      resolveMcpOAuthScopeDiscovery({
        discoveryUrl,
        mcpServerId: server?.id ?? persistedServerId,
        persistedDiscoveryUrl: server?.url ?? null,
      }),
    [persistedServerId, server?.id, server?.url],
  );
  const isPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    credentialMutation.isPending ||
    authorizeMutation.isPending ||
    completeOAuthCallbackMutation.isPending ||
    testMutation.isPending ||
    isAuthorizing;
  const testServerConnection = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const result = await testMutation.mutateAsync(id);

        if (result.success) {
          toasterApi.success({
            title: t('testSuccess'),
            description: t('testSuccessDescription', {
              toolCount: result.tools.length,
              latencyMs: result.latencyMs,
            }),
          });

          return true;
        }

        toasterApi.error({
          title: t('testFailed'),
          description: result.error ?? '',
        });

        return false;
      } catch {
        toasterApi.error({
          title: t('testError'),
          description: t('testErrorDescription'),
        });

        return false;
      }
    },
    [testMutation, t],
  );
  const authorizeServer = useCallback(
    async (id: string) => {
      const authorizeUrl = await authorizeMutation.mutateAsync(id);

      setIsAuthorizing(true);

      try {
        if (
          window.electronAPI?.shell?.openExternal &&
          window.electronAPI.mcpOAuth
        ) {
          const cancelAuthorizationWaitRef: {
            current: (() => void) | null;
          } = {
            current: null,
          };
          const authorizationResult = new Promise<{
            code?: string;
            state?: string;
          }>((resolve, reject) => {
            let unsubscribeSuccess: (() => void) | null = null;
            let unsubscribeError: (() => void) | null = null;
            let timeout: number | null = null;
            const finish = () => {
              if (timeout !== null) window.clearTimeout(timeout);
              unsubscribeSuccess?.();
              unsubscribeError?.();
            };

            cancelAuthorizationWaitRef.current = finish;
            timeout = window.setTimeout(() => {
              finish();
              reject(new Error(t('oauthAuthorizationTimedOut')));
            }, 120000);
            const handleSuccess = (payload: {
              code?: string;
              state?: string;
            }) => {
              finish();
              resolve(payload);
            };

            const handleError = ({ error }: { error: string }) => {
              finish();
              reject(new Error(error || t('oauthAuthorizationFailed')));
            };

            unsubscribeSuccess =
              window.electronAPI.mcpOAuth.onSuccess(handleSuccess);
            unsubscribeError = window.electronAPI.mcpOAuth.onError(handleError);
          });
          const result =
            await window.electronAPI.shell.openExternal(authorizeUrl);

          if (!result.success) {
            cancelAuthorizationWaitRef.current?.();

            throw new Error(result.error ?? t('oauthAuthorizationFailed'));
          }

          const callbackPayload = await authorizationResult;

          if (callbackPayload.code && callbackPayload.state) {
            await completeOAuthCallbackMutation.mutateAsync({
              code: callbackPayload.code,
              state: callbackPayload.state,
            });
          }

          return;
        }

        const popup = window.open(
          authorizeUrl,
          'mcp-oauth',
          'width=600,height=700,scrollbars=yes',
        );

        if (!popup) throw new Error(t('oauthPopupBlocked'));
        const callbackPayload = await waitForMcpOAuthPopupCallback({
          popup,
          authorizationFailedMessage: t('oauthAuthorizationFailed'),
          authorizationTimedOutMessage: t('oauthAuthorizationTimedOut'),
          authorizationWindowClosedMessage: t('oauthAuthorizationWindowClosed'),
        });

        if (callbackPayload.code && callbackPayload.state) {
          await completeOAuthCallbackMutation.mutateAsync({
            code: callbackPayload.code,
            state: callbackPayload.state,
          });
        }
      } finally {
        setIsAuthorizing(false);
      }
    },
    [authorizeMutation, completeOAuthCallbackMutation, t],
  );
  const handleSave = useCallback(async () => {
    if (!name.trim()) {
      toasterApi.error({ title: t('validationNameRequired'), description: '' });

      return;
    }

    if (!url.trim()) {
      toasterApi.error({ title: t('validationUrlRequired'), description: '' });

      return;
    }

    const credential = draftCredential;
    const originalCredentialType = toMcpFormCredentialType(
      server?.credentialType ?? null,
    );

    if (
      credentialType !== 'NONE' &&
      !credential &&
      (!isEditing || originalCredentialType !== credentialType)
    ) {
      toasterApi.error({
        title: t('validationCredentialsRequired'),
        description: '',
      });

      return;
    }

    try {
      let savedId = server?.id ?? persistedServerId;
      let oauthAuthorizationFailed = false;

      if (savedId) {
        await updateMutation.mutateAsync({
          id: savedId,
          data: {
            name,
            transport: 'HTTP',
            url,
            requiresApproval,
          },
        });
      } else {
        const createPayload: CreateMcpServerDto = {
          name,
          transport: 'HTTP',
          url,
          args: [],
          scope: 'USER',
          requiresApproval,
          allowedTargetTypes: ['COPILOT'],
        };
        const created = await createMutation.mutateAsync(createPayload);

        savedId = created.id;
        setPersistedServerId(created.id);
      }

      if (credential && savedId) {
        await credentialMutation.mutateAsync({ id: savedId, credential });
        if (credential.type === 'OAUTH' || credential.type === 'OAUTH_DCR') {
          try {
            await authorizeServer(savedId);
            await testServerConnection(savedId);
          } catch (authorizationError) {
            oauthAuthorizationFailed = true;
            toasterApi.error({
              title: t('oauthAuthorizationIncomplete'),
              description:
                authorizationError instanceof Error
                  ? authorizationError.message
                  : '',
            });
          }
        }
      }

      if (oauthAuthorizationFailed) return;
      toasterApi.success({
        title: isEditing ? t('saveSuccess') : t('addSuccess'),
        description: name,
      });
      onClose();
    } catch (error) {
      toasterApi.error({
        title: t('saveError'),
        description: error instanceof Error ? error.message : t('tryAgain'),
      });
    }
  }, [
    name,
    url,
    requiresApproval,
    credentialType,
    isEditing,
    server,
    createMutation,
    updateMutation,
    credentialMutation,
    persistedServerId,
    authorizeServer,
    testServerConnection,
    onClose,
    draftCredential,
    t,
  ]);

  return (
    <SettingsEditDialog
      open
      title={isEditing ? t('titleEdit') : t('titleAdd')}
      description={server?.name}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !isPending) onClose();
      }}
      onSubmit={handleSave}
      loading={isPending}
      submitText={isEditing ? t('save') : t('addServer')}
      cancelText={t('cancel')}
      contentMaxW="560px"
      contentMaxHeight="60vh"
      withFooterScrollShadow
    >
      <VStack gap="4" alignItems="stretch" w="full">
        <Field.Root w="full">
          <Field.Label>{t('fieldName')}</Field.Label>
          <Input
            size="sm"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('placeholderName')}
            disabled={isPending}
          />
        </Field.Root>

        <Field.Root w="full">
          <Field.Label>{t('fieldUrl')}</Field.Label>
          <Input
            size="sm"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://mcp.example.com/mcp"
            disabled={isPending}
          />
        </Field.Root>

        <McpCredentialFields
          discoveryUrl={url}
          isEditing={isEditing}
          initialCredentialType={server?.credentialType}
          onCredentialChange={setDraftCredential}
          onCredentialTypeChange={setCredentialType}
          disabled={isPending}
          discoverOAuthScopes={handleDiscoverOAuthScopes}
        />

        <HStack w="full" justify="space-between" alignItems="center" gap="4">
          <VStack gap="0" alignItems="flex-start">
            <BodyStrong>{t('fieldRequireApproval')}</BodyStrong>
            <MetaDefault color="fg.muted">
              {t('fieldRequireApprovalHelper')}
            </MetaDefault>
          </VStack>
          <SettingsBooleanControl
            checked={requiresApproval}
            onCheckedChange={(details) => setRequiresApproval(details.checked)}
            disabled={isPending}
          />
        </HStack>
      </VStack>
    </SettingsEditDialog>
  );
}
